import {MutableRefObject, useContext, useEffect, useState} from 'react';
import {useTypedFastStorage} from '../../hooks/useFastStorage';
import CheckBox from "../../ui/CheckBox";
import SecondaryButton from "../../ui/SecondaryButton";
import {ApplicableTableSettings} from "../../tableSettings";
import SmartTable from "../../uiControls/SmartTable";
import {DateRenderer, FloatRenderer, NullableCheckboxIconRenderer, TextAlignLeftRenderer} from "../../uiControls/SmartTableRenderers";
import _ from "lodash";
import RadioButton from "../../ui/RadioButton";
import {batchPrintTable} from "./tableSettings";
import {JobNames} from "../../framework.core/JobNames";
import GreenButton from "../../ui/GreenButton";
import Fieldset from "../common/Fieldset";
import Date from "../../ui/Date";
import BranchDropDown from "../../uiControls/BranchDropDown";
import {now} from "../../dateUtils";
import ApplicationContext from "../../ApplicationContext";
import {DocumentType} from "../../framework.core/DocumentType";
import {BatchPrintViewModel} from "../../fms.core/report/BatchPrintViewModel";
import useGlobalOkDialog from '../../hooks/useGlobalOkDialog';
import useReportViewer from '../../hooks/useReportViewer';
import { CRDBArgs, InvoiceArgs } from '../../fms.report/args';
import useGlobalYesNoDialog from '../../hooks/useGlobalYesNoDialog';
import Number from "../../ui/Number";
import TextBox from '../../ui/TextBox';

interface IBatchPrint {
  [key: string]: any,
  list: BatchPrintViewModel[]
}

interface BatchPrintStorageKeys {
  modelStorage: string,
};

interface BatchPrintViewParams {
  id: string | undefined,
  onPopupClose?: () => void,
  selectedElement: any,
  jobName: JobNames,
  documentType: DocumentType
};

interface BatchPrintLoadedDetailsViewParams {
  batchPrint: IBatchPrint,
  batchPrintRef: MutableRefObject<IBatchPrint | null>,
  setBatchPrint: (x: Partial<IBatchPrint>) => void,
  onPopupClose?: () => void,
  documentType: DocumentType,
  loadList: () => void
};

function getBatchPrintStorageKeys(): BatchPrintStorageKeys {
  return {modelStorage: 'common-more-arrival-notice'}
}

function BatchPrintImpl(params: BatchPrintViewParams) {

  const {modelStorage} = getBatchPrintStorageKeys();
  const {selectedElement, documentType} = params;

  const [inStorage, setInStorage, removeBatchPrintFromStorage, batchPrintRef] = useTypedFastStorage<IBatchPrint>(modelStorage, null);
  const appContext = useContext(ApplicationContext);

  useEffect(() => {
      initialize();
      return () => removeBatchPrintFromStorage()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedElement?.id]
  );

  async function loadExisting(): Promise<IBatchPrint> {
    return {
      sendTo: 'E',
      rangeType: 'D',
      dateFrom: now(),
      dateTo: now(),
      branch: 'ALL', //selectedElement?.branch || '',
      list: []
    }
  }

  async function loadList() {
    const apiResult = await appContext.api.readBatchPrintList(documentType, inStorage?.rangeType, inStorage?.dateFrom, inStorage?.dateTo, inStorage?.numberFrom, inStorage?.numberTo,  inStorage?.branch);
    if(apiResult) {
      apiResult.forEach((item:any) => {
      item.selected = true;
      });
      setInStorage({list:apiResult})      
    }
  }

  async function initialize() {
    const data = await loadExisting();
    setInStorage({...data})
   
  }

  if (!inStorage) {
    return null;
  }

  return (
    <BatchPrintInitializedImpl
      batchPrint={inStorage}
      batchPrintRef={batchPrintRef}
      setBatchPrint={setInStorage}
      onPopupClose={params.onPopupClose}
      documentType={documentType}
      loadList={loadList}
    />
  );
}

function SelectRenderer({data, row}: any) {
  const toggleSelection = data[row.index].toggleSelection;
  const isView = data[row.index].isView;

  return <div style={{width: '100%'}}>
    <CheckBox value={data[row.index].selected}
              onChange={() => toggleSelection(row.index)} disabled={isView}/>
  </div>;
}

function BatchPrintInitializedImpl({
                                     batchPrint,
                                     batchPrintRef,
                                     setBatchPrint,
                                     onPopupClose,
                                     documentType,
                                     loadList,
                                   }: BatchPrintLoadedDetailsViewParams) {
  const [selectedIndex, setSelectedIndex] = useState(0)
  const appContext = useContext(ApplicationContext)
  const [showGlobalOkDialog] = useGlobalOkDialog();
  const [showGlobalYesNoDialog] = useGlobalYesNoDialog();
  const {viewReport} = useReportViewer();

  function onChange(change: Partial<IBatchPrint>) {
    setBatchPrint(change);
  }

  function toggleSelection(i: number) {
    const _list = _.cloneDeep(batchPrint.list);
    _list[i].selected = !_list[i].selected;

    onChange({list: _list})
  }

  function selectAllInTable() {
    let count = 1;
    const _list = _.cloneDeep(batchPrint.list);
    _list.forEach((item) => {
      if(count <= 100) item.selected = true;
      else item.selected = false;
      count++;}
    );
    onChange({list: _list})
  }

  function clearTable() {
    const _list = _.cloneDeep(batchPrint.list);
    _list.forEach(item => item.selected = false);
    onChange({list: _list})
  }

  function getArgs(item: BatchPrintViewModel) {
    let title;
    let args;
    const str = `${item.cusID}|${item.cusName}|${item.cusContact}|${item.cusFax}|${item.cusEmail}`;
    if (documentType === DocumentType.InvoiceS){
        title = `Invoice [${item.invoiceNo}] for ${item.cusFullName}`;
        args = new InvoiceArgs({
          docType: DocumentType.InvoiceS,
          formCaption: title,
          subject: title,
          tbId: item.id.toString(),
          tbName: "T_INVOHD",
          toCompanyid: str,

          stamp: appContext.suntekSettings?.accounting?.showPastDueOnInv,
          useMultiCur: appContext.suntekSettings?.accounting?.showAmountByCurrencyInInvoice
        });
    } else if(documentType === DocumentType.CrDbBatch){
      title = `CR/DB # [${item.invoiceNo}]`;
      args = new CRDBArgs({
        docType: DocumentType.CrDbBatch,
        formCaption: title,
        subject: title,
        tbId: item.id.toString(),
        toCompanyid: str,
      })
    }
    return args;
  }

  async function checkPrintOptions(isPreview: boolean = true) {
    let errorMsg = 'you must check at least one of select list';
    let title = documentType === DocumentType.InvoiceS ? 'Invoices': 'CrDbs';
    let errorMsg2= `you can select up to 100 ${title}\n but ${batchPrint.list.filter(item =>item.selected).length} ${title} selected\n Do you want to print 100 ${title}? `;
    if (isPreview || batchPrint.sendTo === 'P') {
      if (!batchPrint.list.some(item => item.selected)) {
        await showGlobalOkDialog(<p>{errorMsg}</p>, false, "Error");
        return false;
      }else if(batchPrint.list.filter(item => item.selected).length > 100){
        const doPrint = await showGlobalYesNoDialog(<p>{errorMsg2}</p>,false, 0, true);
        if(doPrint){
          let count = 0;
          batchPrint.list.forEach((x) => {
              if(x.selected){
                count++;
              }
              if(count > 100){
                x.selected = false;
              }
          });
          onChange({list: batchPrint.list});
        } else {
          return false;
        }
      }
      
    } else {
      const selectList = batchPrint.list.filter((item) => item.selected);
      if(selectList.length <= 100){
        if (batchPrint.sendTo === 'F') {
          errorMsg = errorMsg + ` with Fax Number`;
  
          if(!selectList.some(item => item.useFax)) {
            await showGlobalOkDialog(<p>{errorMsg}</p>, false, "Error");
            return false;
          }
        } else if (batchPrint.sendTo === 'E') {
          errorMsg = errorMsg + ` with Email Address`;
  
          if(!selectList.some(item => item.useEmail)) {
            await showGlobalOkDialog(<p>{errorMsg}</p>, false, "Error");
            return false;
          }
        } 
      }else{
        await showGlobalOkDialog(<p>{errorMsg2}</p>, false, "Error");
        return false;
      }
    
    }
    return true;
  }

  async function print() {
    if (!await checkPrintOptions()) return;

    const selectList = batchPrint.list.filter((item) => item.selected);
    let args;
    const batchPrintInfos: { tbName: string, tbId: string }[] = []
    let tTitle: string = '';
    const total = selectList.length;

    for (let i: number = 0; i < selectList.length; i++) {
      const row = selectList[i];
      if (batchPrintInfos.length === 0) {
        tTitle = documentType === DocumentType.InvoiceS ? ` Invoice [${row.invoiceNo}] for ${row.cusFullName}` : ` Credit / Debit [${row.invoiceNo}]`;
        if (total > 1) tTitle += ` + ${total - 1}`;
        batchPrintInfos.push({ tbName: row.tbName, tbId: String(row.id) });
      } else {
        if (batchPrintInfos[batchPrintInfos.length - 1].tbName !== row.tbName) {
          batchPrintInfos.push({ tbName: row.tbName, tbId: String(row.id) });
        } else {
          batchPrintInfos[batchPrintInfos.length - 1].tbId += `,${row.id}`;
        }
      }
    }

    if (documentType === DocumentType.InvoiceS) {
      //let title = `Invoice Batch Print`;
      args = new InvoiceArgs({
        docType: documentType,
        formCaption: tTitle,
        subject: tTitle,
        batchPrintInfos: batchPrintInfos,
        //tbId: selectedIds,
        //tbName: "T_INVOHD",
        //toCompanyid: selectedToCompanyIds,

        stamp: appContext.suntekSettings?.accounting?.showPastDueDateOnInvoice,
        useMultiCur: appContext.suntekSettings?.accounting?.showAmountByCurrencyInInvoice
      });

    } 
    else if (documentType === DocumentType.CrDbBatch) {
      args = new CRDBArgs({
        docType: documentType,
        formCaption: tTitle,
        subject: tTitle,
        batchPrintInfos: batchPrintInfos,
      });
    }
    if (args) viewReport(args, "", true);
  }

// [TodoMark] 240613-mskang Fax버튼 disabled 후 동작구현보류 / Direct Print는 5에서 3.3처럼 바로 저장되게 할 수 없기 때문에 Preview로 변경
  async function send() {
    if(!await checkPrintOptions(false)) return;

    const selectList = batchPrint.list.filter((item) => item.selected);
    for (let i = 0; i < selectList.length; i++) {
      switch(batchPrint.sendTo) {
        case "E":
          if(selectList[i].useEmail) {
            const args = getArgs(selectList[i]);
            if(args) {
              const apiResult = await appContext.api.sendEMail(args);
              if (!apiResult || !apiResult.success) {
                await showGlobalOkDialog(<p>{apiResult?.message??"Failed to send email"}</p>, false, "Error");
              }
            }
          }
          break;
        case "F":
          await showGlobalOkDialog(<p>{"not implemented"}</p>, false, "Error");  
          break;
      }
    }
  };

  const invoiceNoHeaderLabel = documentType === DocumentType.CrDbBatch ? 'CrDb #' : 'Invoice #';
  const snameHeaderLabel = documentType === DocumentType.CrDbBatch ? 'Agent' : 'Invoice To';

  const tableOptions: ApplicableTableSettings = {
    ...batchPrintTable,
    selectable: true,
    rowProps: (row: any) => ({onClick: () => setSelectedIndex(row.index)}),
    selectedRowIndex: selectedIndex,
    tableName: 'batchPrintTable',
    data: batchPrint.list.map((x, i) => ({...x, i, toggleSelection})),
    rowRenderers: [
      {accessor: 'selected', Cell: SelectRenderer},
      {accessor: 'invoiceNo', Cell: TextAlignLeftRenderer, Header: () => <span>{invoiceNoHeaderLabel}</span>},
      {accessor: 'invoiceDate', Cell: DateRenderer},
      {accessor: 'sName', Cell: TextAlignLeftRenderer, Header: () => <span>{snameHeaderLabel}</span>},
      {accessor: 'salseperson', Cell: TextAlignLeftRenderer},
      {accessor: 'invoiceAmt', Cell: FloatRenderer},
      {accessor: 'useFax', Cell: NullableCheckboxIconRenderer},
      {accessor: 'useEmail', Cell: NullableCheckboxIconRenderer},
    ],
  };

  let sendButtonLabel = 'SEND';
  if (batchPrint.sendTo === 'E') {
    sendButtonLabel += ' EMAIL'
  } else if (batchPrint.sendTo === 'F') {
    sendButtonLabel += ' FAX'
  }

  return (
    <div className='report-popup'>

      <div style={{ display: 'flex' }}>
        <div style={{ width: '60px' }}><RadioButton group="rangeType" reverse label="Date" checked={!batchPrint.rangeType || batchPrint.rangeType === 'D'} onChecked={() => onChange({ rangeType: 'D' })} /></div>
        <div style={{ width: '65px', marginRight: '5px' }}><RadioButton group="rangeType" reverse label="Number" checked={batchPrint.rangeType === 'N'} onChecked={() => onChange({ rangeType: 'N' })} /></div>
    
        {(!batchPrint.rangeType || batchPrint.rangeType === 'D') ? <>
          <div style={{ width: '80px' }}>
            <Date value={batchPrint.dateFrom} onChange={dateFrom => onChange({ dateFrom })} />
          </div>

          <div style={{ width: '40px', textAlign: 'center' }}>
            ~
          </div>

          <div style={{ width: '80px' }}>
            <Date value={batchPrint.dateTo} onChange={dateTo => onChange({ dateTo })} />
          </div>

        </> : <>
          <div style={{ width: '80px' }}>
            <TextBox  value={batchPrint.numberFrom} onChange={e => onChange({ numberFrom: e.target.value })} />
          </div>


          <div style={{ width: '40px', textAlign: 'center' }}>
            ~
          </div>

          <div style={{ width: '80px' }}>
            <TextBox  value={batchPrint.numberTo} onChange={e => onChange({ numberTo: e.target.value })} />
          </div>
        </>}


        <div style={{marginLeft: '150px', width: '100px'}}>
          {appContext.suntekSettings?.company?.enableBranch &&
            <>Branch</>
          }
        </div>

        <div style={{width: '110px'}}>
          {appContext.suntekSettings?.company?.enableBranch &&
            <BranchDropDown
              value={batchPrint?.branch}
              onChange={branch => onChange({branch})}
              addAll={true}
            />
          }
        </div>
      </div>

      <div style={{marginTop: '10px'}}>
        <div style={{display: 'flex'}}>
          <div style={{marginLeft: '30px', fontWeight: 'bold', position: 'relative', top: '15px'}}>
            Select List
          </div>
          <div style={{marginLeft: '30px', fontWeight: 'bold', position: 'relative', top: '15px'}}>
            Total Items: {batchPrint.list.length}
          </div>
          <div style={{marginLeft: '30px', fontWeight: 'bold', position: 'relative', top: '15px'}}>
            Selected Items: {batchPrint.list.filter(item => item.selected).length}
          </div>

          <div style={{margin: 'auto'}}></div>

          <div style={{
            display: "flex",
            float: 'right',
            justifyContent: 'space-between',
            width: 160,
            paddingBottom: 5,
            marginTop: 10
          }}>
            <GreenButton style={{ width: '55px' }} size='extrasmall' onClick={selectAllInTable}>ALL</GreenButton>
            <GreenButton style={{ width: '55px', marginLeft: '5px' }} size='extrasmall' onClick={clearTable}>CLEAR</GreenButton>
            <GreenButton style={{ width: '55px', marginLeft: '5px' }} size='extrasmall' onClick={() => {
              loadList()
            }}>LIST</GreenButton>
          </div>
        </div>

        <SmartTable {...tableOptions} />
      </div>

      <div style={{marginTop: '10px'}}>
        <Fieldset label={''}>
          <div style={{display: 'flex', marginTop: '10px'}}>
            <div style={{marginLeft: '200px', width: '240px'}}>
              <RadioButton group="send-to" reverse label="E-Mail" checked={batchPrint.sendTo === 'E'} onChecked={() => onChange({sendTo: 'E'})} />
            </div>

            <div style={{width: '238px'}}>
              <RadioButton group="send-to" reverse label="Fax" boldOnActive checked={batchPrint.sendTo === 'F'} disabled onChecked={() => onChange({sendTo: 'F'})} />
            </div>
          </div>
        </Fieldset>
      </div>

      <div className='report-footer'>
        <SecondaryButton
          size="regular"
          style={{width: '90px'}}
          onClick={() => print()}>
          PREVIEW
        </SecondaryButton>
        <SecondaryButton
          size="regular"
          style={{width: '90px'}}
          onClick={() => { 
            if (batchPrint.sendTo === 'P') print();
            else send();
          }}>
          {sendButtonLabel}
        </SecondaryButton>
        <SecondaryButton
          size="regular"
          style={{width: '90px'}}
          onClick={onPopupClose}>
          CANCEL
        </SecondaryButton>
      </div>
    </div>
  );
}

const BatchPrintDetails = ({
                             onPopupClose,
                             selectedElement,
                             jobName,
                             documentType,
                           }: { onPopupClose?: () => void, selectedElement: any, documentType: DocumentType, jobName: JobNames }) => {
  if (!selectedElement) return null;

  /**
   * Implementation note:
   *   key is important in this case in order to force full List view rerender for different parameters.
   */

  return (
    <BatchPrintImpl id={''} jobName={jobName}
                    documentType={documentType}
                    selectedElement={selectedElement}
                    onPopupClose={onPopupClose}
                    key={`batchPrint-details`}/>
  );
};
export default BatchPrintDetails;
